import type { CapabilityCode } from '@railroute/contracts/core';

/**
 * Разделы панели и право, на котором каждый держится.
 *
 * ВТОРОЙ МОДЕЛИ ПРАВ НЕТ. Право раздела — та же капабилити, которую требует
 * ручка API под ним (`apps/api/src/admin/admin.controller.ts`,
 * `work-orders/form344.controller.ts`). Панель ничего не решает сама: она
 * показывает ровно то, что сервер и так пропустит, и прячет то, на чём он и
 * так откажет. Расхождение здесь было бы не украшением, а ложью — раздел,
 * открывающийся в отказ, или спрятанная работа, которую человек вправе делать.
 *
 * РАЗДЕЛ ПОЯВЛЯЕТСЯ ВМЕСТЕ С СОДЕРЖИМЫМ. Пунктов этапа больше, чем разделов в
 * этом списке: проверка знаний, инструктажи, журналы, ЭЦП в браузере ещё не
 * сделаны. Заводить им пустые пункты меню нельзя — меню, наполовину ведущее в
 * «скоро будет», перестаёт отвечать на вопрос, что в панели есть.
 */
export type SectionId =
  | 'onboard'
  | 'work-order-issue'
  | 'work-order'
  | 'route-sheets'
  | 'personnel'
  | 'briefing-journal'
  | 'subdivisions'
  | 'organization';

export interface PanelSection {
  id: SectionId;
  /** Название в меню — словами работы, а не кодом права. */
  title: string;
  /** Одна строка под заголовком раздела: что здесь делают. */
  hint: string;
  /** Капабилити, которую требует ручка API под разделом. */
  requires: CapabilityCode;
  /**
   * Другие права, любое из которых тоже открывает раздел, — когда ручка под
   * ним принимает несколько (issue #510: листы читает и бухгалтерия по
   * `ROUTE_SHEET_READ`, у которой может не быть `WORK_ORDER_READ`).
   */
  orRequires?: readonly CapabilityCode[];
}

export const PANEL_SECTIONS: readonly PanelSection[] = [
  {
    id: 'personnel',
    title: 'Персонал',
    hint: 'Работники организации, их табельные номера и подразделения.',
    requires: 'PERSONNEL_READ',
  },
  {
    id: 'briefing-journal',
    // Право — `BRIEFING_JOURNAL_READ`, то же, которое требует ручка под
    // разделом (`GET /work-orders/briefing-journal`). Решение Штаба
    // 30.09.2026: журнал — по своему праву, а не по праву видеть персонал
    // (его держит и администратор организации, которому наряды не открыты,
    // #440). Охват и номера нарядов в строках решает сервер.
    title: 'Журнал инструктажа',
    hint: 'Журнал регистрации инструктажа по форме приложения 17 — за период, для печати и выгрузки в Excel.',
    requires: 'BRIEFING_JOURNAL_READ',
  },
  {
    id: 'subdivisions',
    title: 'Подразделения',
    hint: 'Структура организации: дистанции, участки, бригады.',
    requires: 'STRUCTURE_MANAGE',
  },
  {
    id: 'organization',
    title: 'Организация',
    hint: 'Карточка организации и её настройки.',
    requires: 'ORGANIZATION_CONFIG_MANAGE',
  },
  {
    id: 'work-order-issue',
    // Право — `WORK_ORDER_ISSUE`, ровно то же, что требует ручка под разделом
    // (`apps/api/src/work-orders/work-orders.controller.ts`, `POST /work-orders`).
    // Кто в организации выдающий — назначение профиля, а не строка в коде:
    // панель о профилях ничего не знает и знать не должна.
    title: 'Выписка наряда',
    hint: 'Мероприятия по пунктам 5.1–5.10 бланка и состав бригады — за столом, а не в поле.',
    requires: 'WORK_ORDER_ISSUE',
  },
  {
    id: 'work-order',
    // Право — `WORK_ORDER_READ`, решение владельца 19.09.2026 («самое узкое из
    // возможного», apps/api/src/work-orders/form344.controller.ts). Сузить его
    // здесь до выдающего наряд нельзя: это отменило бы решение владельца
    // молча, правкой в клиенте. Следствие названо прямо, чтобы следующий не
    // «чинил» его наугад: работник, у которого это право есть и в телефоне,
    // видит в панели этот раздел — и только его.
    title: 'Наряд-допуск',
    hint: 'Наряд по номеру — лист приложения 1 к Правилам № 344 для просмотра и печати.',
    requires: 'WORK_ORDER_READ',
  },
  {
    id: 'route-sheets',
    // Право — `WORK_ORDER_READ`, то же, которое требуют ручки под разделом
    // (`apps/api/src/route-sheets/route-sheets.controller.ts`,
    // `GET /rail/route-sheets/my`). Лист есть свод поездок за месяц, и
    // отдельного права читать его не заведено. ЧЕЙ лист виден — решает сервер
    // (`route-sheet-visibility.ts`), а не панель: своё — работник, своих —
    // руководитель, тех, кому выдаёт, — начальник цеха.
    //
    // ВЫДАЧИ ЗДЕСЬ НЕТ и не будет: выдача подписывается ключом ТЕЛЕФОНА
    // (ADR-008 §7, уровень INTERNAL_DEVICE), а такого ключа в браузере не
    // существует. Кнопка, ведущая в отказ, хуже её отсутствия.
    title: 'Маршрутные листы',
    hint: 'Маршрутные листы учёта разъездов за месяц по форме ФРУ № 12: состояние, утверждение к оплате, печать и Excel.',
    requires: 'WORK_ORDER_READ',
    // Issue #510: бухгалтерия читает и выгружает утверждённые листы по своему
    // праву (ADR-008 §10) — то же «любое из двух», что у ручек под разделом.
    orRequires: ['ROUTE_SHEET_READ'],
  },
  // Право БЭМ, а не ROUTE_ASSIGN: график и рейсы ведут мастер и руководитель; у начальника ПТО
  // ROUTE_ASSIGN есть ради маршрутов, и раздел, открывающийся ему в отказ, был бы ложью меню.
  { id: "onboard", title: "БЭМ", hint: "Рейсы, обходы, дефекты и ремонт", requires: "ONBOARD_SCHEDULE_MANAGE" },
];

/**
 * Права ДЕЙСТВИЙ внутри разделов (issue #445).
 *
 * Раздел держится на праве чтения, а кнопка внутри него — на своём праве, и
 * оба названы здесь, в одном файле. Написать код права прямо в компоненте
 * значило бы завести вторую модель прав там, где её труднее всего заметить:
 * перечень разделов говорил бы одно, а кнопка — другое, и разошлись бы они
 * молча.
 *
 * Право то же, которое требует ручка API под кнопкой
 * (`apps/api/src/work-orders/work-order-approval.controller.ts`). Панель
 * ничего не решает сама: показать кнопку тому, кому сервер откажет, значит
 * соврать, а спрятать у того, кто вправе, — отнять работу.
 */
export const SECTION_ACTIONS = {
  /** Согласовать наряд — оперативное разрешение на работы в своей сети. */
  approveWorkOrder: 'WORK_ORDER_APPROVE',
  /**
   * Открыть лист ДРУГОГО работника (issue #508). Право то же, на котором стоит
   * список людей подразделения (`GET /users/subdivision-members`): без него
   * выбирать некого, и раздел показывает только свой лист.
   *
   * Правом ЧИТАТЬ чужой лист это не является — читателя решает сервер. Здесь
   * оно отвечает на другой вопрос: показывать ли выбор работника вообще.
   */
  pickRouteSheetWorker: 'ROUTE_ASSIGN',
  /**
   * «Утверждаю к оплате» маршрутного листа — ЭЦП руководителя дистанции
   * (issue #509, #510). Право то же, что держит ручка подписи; ЧЕЙ лист он
   * утверждает, решает сервер по области назначения.
   */
  approveRouteSheet: 'ROUTE_SHEET_APPROVE',
  /**
   * Внести или продлить допуск работника (issue #524). Право то же, которое
   * требует ручка под кнопкой (`POST /clearances`,
   * `apps/api/src/clearances/clearances.controller.ts`).
   *
   * Даёт его роль «ответственный за допуски» (`CLEARANCE_OFFICER`), а не
   * должность: у инженера по охране труда она стоит по умолчанию, но снимается
   * отдельно, и снятая роль не перестаёт делать его инженером. Поэтому кнопка
   * держится на праве, а не на профиле: спроси панель профиль — она показала бы
   * кнопку тому, у кого роль сняли.
   */
  manageClearanceEvidence: 'CLEARANCE_EVIDENCE_MANAGE',
  /**
   * Должность сотрудника (issue #531/#533). Право то же, которое требует
   * ручка правки сотрудника (`PATCH /admin/memberships/:id`,
   * `apps/api/src/admin/admin.controller.ts`).
   */
  editPosition: 'PERSONNEL_MANAGE',
} as const satisfies Record<string, CapabilityCode>;

/** Вправе ли этот набор прав согласовать наряд. */
export function mayApproveWorkOrder(capabilities: readonly CapabilityCode[]): boolean {
  return capabilities.includes(SECTION_ACTIONS.approveWorkOrder);
}

/** Показывать ли кнопку «Утверждаю к оплате» у подтверждённого листа. */
export function mayApproveRouteSheet(capabilities: readonly CapabilityCode[]): boolean {
  return capabilities.includes(SECTION_ACTIONS.approveRouteSheet);
}

/** Показывать ли в разделе листов выбор работника, а не только свой лист. */
export function mayPickRouteSheetWorker(capabilities: readonly CapabilityCode[]): boolean {
  return capabilities.includes(SECTION_ACTIONS.pickRouteSheetWorker);
}

/** Вправе ли этот набор прав вносить и продлевать допуски работников. */
export function mayManageClearanceEvidence(capabilities: readonly CapabilityCode[]): boolean {
  return capabilities.includes(SECTION_ACTIONS.manageClearanceEvidence);
}

/**
 * Разделы, доступные с этим набором прав, в порядке перечня выше.
 *
 * Набор прав приходит с сервера (`GET /auth/context`) и относится к ОДНОМУ
 * членству: у человека, работающего в двух организациях, права в них разные, и
 * складывать их нельзя.
 */
export function sectionsFor(capabilities: readonly CapabilityCode[], membership: MembershipHints = {}): PanelSection[] {
  const granted = new Set<CapabilityCode>(capabilities);
  return PANEL_SECTIONS.filter(
    (section) =>
      (granted.has(section.requires) || (section.orRequires ?? []).some((code) => granted.has(code))) &&
      // Стенд 30.09.2026: «Наряд-допуск» у того, кому читать в нём нечего
      // (право только на свои наряды, а своих нет), — пустой раздел. Решает
      // сервер (`workOrderSectionVisible`); старый сервер поля не шлёт — как было.
      !(section.id === 'work-order' && membership.workOrderSectionVisible === false),
  );
}

/** Подсказки сервера о членстве, кроме прав (`GET /auth/context`). */
export interface MembershipHints {
  workOrderSectionVisible?: boolean;
}

/**
 * Раздел, который откроется первым. `null` — разделов нет вовсе; для такого
 * человека панель пуста, и сказать это надо прямо, а не показать пустой экран.
 */
export function firstSection(capabilities: readonly CapabilityCode[], membership: MembershipHints = {}): SectionId | null {
  return sectionsFor(capabilities, membership)[0]?.id ?? null;
}

/**
 * Остаётся ли открытый раздел доступным после смены организации. Права в
 * другой организации другие, и удержать на экране раздел, на который их там
 * нет, значит показать отказ вместо содержимого.
 */
export function keepSection(
  current: SectionId | null,
  capabilities: readonly CapabilityCode[],
  membership: MembershipHints = {},
): SectionId | null {
  if (current !== null && sectionsFor(capabilities, membership).some((section) => section.id === current)) {
    return current;
  }
  return firstSection(capabilities, membership);
}

/** Вправе ли этот набор прав менять должность сотрудника. */
export function mayEditPosition(capabilities: readonly CapabilityCode[]): boolean {
  return capabilities.includes(SECTION_ACTIONS.editPosition);
}
